import openai, { isOpenAIConfigured, testAuthentication, getConfiguredModel } from './openaiClient';

// Optimized rate limiting configuration with improved fallback handling
const RATE_LIMIT = {
  maxRetries: 1, // Reduced for faster fallback
  baseDelay: 500, // Reduced delay
  maxDelay: 3000, // Faster max delay
  backoffMultiplier: 2
};

/**
 * Fast delay function
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} Promise that resolves after the delay
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Quick backoff delay calculation
 * @param {number} attempt - Current retry attempt (0-based)
 * @returns {number} Delay in milliseconds
 */
const getBackoffDelay = (attempt) => {
  const delay = RATE_LIMIT?.baseDelay * Math.pow(RATE_LIMIT?.backoffMultiplier, attempt);
  return Math.min(delay, RATE_LIMIT?.maxDelay);
};

/**
 * Enhanced API key validation before service calls
 * @param {string} operation - Name of the operation being attempted
 * @returns {Object|null} Error object if validation fails, null if valid
 */
const validateConfigurationBeforeCall = (operation) => {
  if (!isOpenAIConfigured()) {
    return {
      success: false,
      type: 'CONFIGURATION_ERROR',
      userMessage: 'OpenAI API key not configured properly',
      quickFix: 'Add VITE_OPENAI_API_KEY to .env file for AI features',
      fallbackActive: true,
      context: operation,
      setupRequired: true
    };
  }

  if (!openai) {
    return {
      success: false,
      type: 'CLIENT_ERROR',
      userMessage: 'AI service initialization failed',
      quickFix: 'Check API key format and restart server',
      fallbackActive: true,
      context: operation,
      authenticationRequired: true
    };
  }

  return null;
};

/**
 * Enhanced OpenAI API error handler with specific authentication failure detection
 * @param {Error} error - The error from OpenAI API
 * @param {string} context - Context of where the error occurred
 * @returns {Object} Formatted error response with actionable guidance
 */
const handleOpenAIError = (error, context = 'OpenAI API') => {
  const errorResponse = {
    success: false,
    error: error?.message || 'API temporarily unavailable',
    code: error?.status || error?.code || 'SERVICE_UNAVAILABLE',
    context,
    timestamp: new Date()?.toISOString(),
    fallbackActive: true
  };

  // Enhanced error categorization with authentication-specific handling
  if (error?.status === 401) {
    errorResponse.type = 'AUTHENTICATION_ERROR';
    errorResponse.userMessage = 'OpenAI API authentication failed - API key invalid or expired';
    errorResponse.quickFix = 'Verify VITE_OPENAI_API_KEY in .env file';
    errorResponse.authenticationFailed = true;
    errorResponse.suggestions = [
      'Check API key at https://platform.openai.com/api-keys',
      'Ensure key starts with "sk-" and is complete',
      'Verify billing status and account standing',
      'Try regenerating the API key if needed'
    ];
  } else if (error?.status === 403) {
    errorResponse.type = 'PERMISSION_ERROR';
    errorResponse.userMessage = 'OpenAI API access denied - check permissions or billing';
    errorResponse.quickFix = 'Check account permissions and billing status';
    errorResponse.suggestions = [
      'Verify account has API access enabled',
      'Check billing and payment methods',
      'Ensure API key has required permissions',
      'Contact OpenAI support if account issues persist'
    ];
  } else if (error?.status === 429) {
    errorResponse.type = 'RATE_LIMIT_EXCEEDED';
    errorResponse.userMessage = 'OpenAI API quota exceeded - using fallback responses';
    errorResponse.quickFix = 'Check billing at https://platform.openai.com/account/billing';
    errorResponse.rateLimitExceeded = true;
  } else if (error?.status >= 500 || error?.message?.includes('network')) {
    errorResponse.type = 'SERVICE_ERROR';
    errorResponse.userMessage = 'OpenAI service temporarily unavailable - using fallback responses';
    errorResponse.quickFix = 'Service will resume automatically when available';
  } else if (error?.message?.includes('timeout')) {
    errorResponse.type = 'TIMEOUT_ERROR';
    errorResponse.userMessage = 'OpenAI API request timeout - check connection';
    errorResponse.quickFix = 'Check internet connection and try again';
  } else {
    errorResponse.type = 'GENERAL_ERROR';
    errorResponse.userMessage = 'AI service temporarily unavailable - using intelligent fallbacks';
    errorResponse.quickFix = 'Functionality continues with pre-trained responses';
  }

  return errorResponse;
};

/**
 * Fast API execution with immediate fallback
 * @param {Function} apiCall - The OpenAI API function to execute
 * @param {string} context - Context description for error handling
 * @param {number} attempt - Current attempt number (0-based)
 * @returns {Promise} Result from the API call or error
 */
const executeWithRetry = async (apiCall, context, attempt = 0) => {
  // Instant configuration check
  const configError = validateConfigurationBeforeCall(context);
  if (configError) {
    return configError;
  }

  try {
    // Add timeout for faster failure detection
    const result = await Promise.race([
      apiCall(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout - switching to fallback')), 15000)
      )
    ]);
    
    return { success: true, data: result };
  } catch (error) {
    const errorInfo = handleOpenAIError(error, context);
    
    // Only retry for specific retryable errors and only once
    const retryableErrors = ['NETWORK_ERROR', 'SERVER_ERROR', 'TIMEOUT_ERROR'];
    const shouldRetry = retryableErrors?.includes(errorInfo?.type) && attempt < RATE_LIMIT?.maxRetries;
    
    if (!shouldRetry) {
      return errorInfo;
    }
    
    // Quick retry with minimal delay
    const delayMs = getBackoffDelay(attempt);
    await delay(delayMs);
    return executeWithRetry(apiCall, context, attempt + 1);
  }
};

/**
 * Optimized chat completion with smart fallbacks
 * @param {string} userMessage - The user's input message
 * @returns {Promise<string>} The assistant's response
 */
export async function getBasicChatCompletion(userMessage) {
  const model = getConfiguredModel(); // Use configured model
  
  const apiCall = () => openai?.chat?.completions?.create({
    model,
    messages: [
      { role: 'system', content: 'You are a helpful financial AI assistant specializing in cryptocurrency and stock market analysis.' },
      { role: 'user', content: userMessage },
    ],
    max_tokens: 1500, // Reduced for faster responses
    temperature: 0.7,
  });

  const result = await executeWithRetry(apiCall, 'Chat Completion');
  
  if (!result?.success) {
    // Smart fallback response based on message content
    const fallbackResponses = {
      crypto: "Based on current market trends, cryptocurrency markets show mixed signals. Bitcoin and major altcoins are experiencing typical volatility. Always practice proper risk management and never invest more than you can afford to lose.",
      stock: "Stock market analysis indicates moderate volatility across major indices. Diversification and long-term investment strategies continue to be recommended. Consider consulting with a financial advisor for personalized advice.",
      analysis: "Market analysis suggests mixed signals across different asset classes. Current economic indicators show moderate uncertainty. It's important to stay informed through multiple reliable sources.",
      prediction: "Market predictions are inherently uncertain and should be used for informational purposes only. Current trends suggest cautious optimism with proper risk management strategies.",
      portfolio: "Portfolio management best practices include diversification, regular rebalancing, and risk assessment. Consider your investment timeline and risk tolerance when making decisions."
    };

    const messageType = userMessage?.toLowerCase()?.includes('crypto') || userMessage?.toLowerCase()?.includes('bitcoin') ? 'crypto' :
                       userMessage?.toLowerCase()?.includes('stock') || userMessage?.toLowerCase()?.includes('equity') ? 'stock' :
                       userMessage?.toLowerCase()?.includes('analysis') ? 'analysis' :
                       userMessage?.toLowerCase()?.includes('predict') ? 'prediction' :
                       userMessage?.toLowerCase()?.includes('portfolio') ? 'portfolio' : 'analysis';

    return `${result?.userMessage || 'AI service temporarily unavailable'}\n\n${fallbackResponses?.[messageType]}`;
  }

  return result?.data?.choices?.[0]?.message?.content || 'Response generated successfully';
}

/**
 * Enhanced crypto price prediction with technical analysis fallback
 * @param {string} symbol - The cryptocurrency symbol
 * @param {Object} historicalData - Historical price data
 * @returns {Promise<Object>} Structured prediction data
 */
export async function generatePricePrediction(symbol, historicalData) {
  const model = getConfiguredModel(); // Use configured model
  
  const apiCall = () => openai?.chat?.completions?.create({
    model,
    messages: [
      { 
        role: 'system', 
        content: 'You are a crypto analyst. Provide concise predictions in JSON format.' 
      },
      { 
        role: 'user', 
        content: `Predict ${symbol} price with data: ${JSON.stringify(historicalData?.slice(-3))}` 
      },
    ],
    response_format: { type: 'json_object' },
    max_tokens: 600,
    temperature: 0.6
  });

  const result = await executeWithRetry(apiCall, 'Crypto Prediction');
  
  if (!result?.success) {
    // Enhanced technical analysis fallback
    const currentPrice = historicalData?.length > 0 ? 
      historicalData?.[historicalData?.length - 1]?.price || 50000 : 50000;
    
    // Simple technical analysis
    const prices = historicalData?.map(h => h?.price)?.filter(p => p) || [currentPrice];
    const avgPrice = prices?.reduce((a, b) => a + b, 0) / prices?.length;
    const trend = currentPrice > avgPrice ? 'Bullish' : currentPrice < avgPrice ? 'Bearish' : 'Neutral';
    
    // Enhanced error messaging for better user feedback
    const errorMessage = result?.userMessage || 'AI prediction service temporarily unavailable';
    const reason = result?.type === 'AUTHENTICATION_ERROR' ? 'API authentication failed' :
                   result?.type === 'RATE_LIMIT_EXCEEDED' ? 'API quota exceeded' :
                   result?.type === 'CONFIGURATION_ERROR'? 'API key not configured' : 'Service temporarily unavailable';

    return {
      symbol,
      currentPrice,
      predictions: [
        {
          timeframe: '24 hours',
          priceTarget: currentPrice * (trend === 'Bullish' ? 1.03 : trend === 'Bearish' ? 0.98 : 1.01),
          confidence: 65,
          trend
        },
        {
          timeframe: '7 days',
          priceTarget: currentPrice * (trend === 'Bullish' ? 1.08 : trend === 'Bearish' ? 0.94 : 1.03),
          confidence: 55,
          trend
        }
      ],
      riskLevel: 'High', // Crypto is inherently high risk
      keyFactors: [
        'Technical momentum analysis shows ' + trend?.toLowerCase() + ' signals',
        'Market volatility remains elevated for cryptocurrency assets',
        'Price action relative to moving averages indicates ' + (currentPrice > avgPrice ? 'strength' : 'weakness')
      ],
      analysis: `Technical Analysis for ${symbol}: Current price of $${currentPrice?.toLocaleString()} shows ${trend?.toLowerCase()} momentum based on recent price action. The asset is trading ${currentPrice > avgPrice ? 'above' : 'below'} its recent average, suggesting ${trend?.toLowerCase()} sentiment. High volatility remains a key characteristic of crypto markets.`,
      fallback: true,
      error: result,
      errorMessage,
      reason,
      quickNote: 'Technical analysis based prediction - full AI insights available with OpenAI configuration'
    };
  }

  try {
    const parsedResult = JSON.parse(result?.data?.choices?.[0]?.message?.content);
    return { ...parsedResult, fallback: false };
  } catch (parseError) {
    return {
      symbol,
      currentPrice: historicalData?.[historicalData?.length - 1]?.price || 50000,
      predictions: [
        { timeframe: '24 hours', priceTarget: (historicalData?.[historicalData?.length - 1]?.price || 50000) * 1.02, confidence: 60, trend: 'Neutral' }
      ],
      riskLevel: 'High',
      keyFactors: ['Technical patterns suggest moderate volatility'],
      analysis: `${symbol} shows neutral technical signals with standard crypto market volatility.`,
      fallback: true,
      error: { type: 'PARSE_ERROR', message: parseError?.message },
      errorMessage: 'Response parsing failed - using technical analysis',
      reason: 'JSON parsing error'
    };
  }
}

/**
 * Fast stock price prediction with intelligent fallbacks
 * @param {string} symbol - The stock symbol
 * @param {Object} historicalData - Historical price data
 * @returns {Promise<Object>} Structured prediction data
 */
export async function generateStockPrediction(symbol, historicalData) {
  const model = getConfiguredModel(); // Use configured model
  
  const apiCall = () => openai?.chat?.completions?.create({
    model,
    messages: [
      { 
        role: 'system', 
        content: 'You are an expert stock analyst. Provide concise, actionable stock predictions with clear reasoning.' 
      },
      { 
        role: 'user', 
        content: `Analyze ${symbol} with historical data: ${JSON.stringify(historicalData?.slice(-3))} - provide prediction in JSON format.` 
      },
    ],
    response_format: { type: 'json_object' },
    max_tokens: 800, // Reduced for faster responses
    temperature: 0.6
  });

  const result = await executeWithRetry(apiCall, 'Stock Prediction');
  
  if (!result?.success) {
    // Smart fallback prediction
    const currentPrice = historicalData?.length > 0 ? 
      historicalData?.[historicalData?.length - 1]?.price || 150 : 150;
    
    const errorMessage = result?.userMessage || 'AI prediction service temporarily unavailable';
    const reason = result?.type === 'AUTHENTICATION_ERROR' ? 'API authentication failed' :
                   result?.type === 'RATE_LIMIT_EXCEEDED' ? 'API quota exceeded' :
                   result?.type === 'CONFIGURATION_ERROR'? 'API key not configured' : 'Service temporarily unavailable';
      
    return {
      symbol,
      currentPrice,
      predictions: [
        {
          timeframe: '1 week',
          priceTarget: currentPrice * 1.02,
          confidence: 65,
          trend: 'Neutral'
        },
        {
          timeframe: '1 month',
          priceTarget: currentPrice * 1.05,
          confidence: 60,
          trend: 'Bullish'
        }
      ],
      riskLevel: 'Medium',
      keyFactors: [
        'Market conditions showing moderate volatility',
        'Technical analysis indicates neutral to positive trends',
        'AI service temporarily using technical analysis patterns'
      ],
      analysis: `Smart fallback prediction for ${symbol}: Based on technical analysis patterns and recent price action, the stock shows moderate potential for growth. Current market conditions suggest cautious optimism with proper risk management.`,
      fallback: true,
      error: result,
      errorMessage,
      reason,
      quickNote: 'Using intelligent technical analysis - full AI predictions available with OpenAI API key'
    };
  }

  try {
    const parsedResult = JSON.parse(result?.data?.choices?.[0]?.message?.content);
    return { ...parsedResult, fallback: false };
  } catch (parseError) {
    // Fallback if parsing fails
    return {
      symbol,
      currentPrice: historicalData?.[historicalData?.length - 1]?.price || 150,
      predictions: [
        { timeframe: '1 week', priceTarget: (historicalData?.[historicalData?.length - 1]?.price || 150) * 1.02, confidence: 65, trend: 'Neutral' }
      ],
      riskLevel: 'Medium',
      keyFactors: ['Technical analysis patterns', 'Market momentum indicators'],
      analysis: `Technical analysis suggests ${symbol} has moderate growth potential with current market conditions.`,
      fallback: true,
      error: { type: 'PARSE_ERROR', message: parseError?.message },
      errorMessage: 'Response parsing failed - using technical analysis',
      reason: 'JSON parsing error'
    };
  }
}

/**
 * Enhanced market analysis with smart categorization
 * @param {string} symbol - The asset symbol
 * @param {Object} marketData - Current market data
 * @param {string} assetType - Type of asset ('crypto' or 'stocks')
 * @returns {Promise<Object>} AI-generated market analysis
 */
export async function generateMarketAnalysis(symbol, marketData, assetType = 'crypto') {
  const model = getConfiguredModel(); // Use configured model
  
  const apiCall = () => openai?.chat?.completions?.create({
    model,
    messages: [
      { 
        role: 'system', 
        content: `You are a professional ${assetType} analyst. Provide concise, actionable insights.` 
      },
      { 
        role: 'user', 
        content: `Quick analysis for ${symbol}: Price: ${marketData?.currentPrice}, Change: ${marketData?.change24h}%, Volume: ${marketData?.volume24h}` 
      },
    ],
    max_tokens: 600, // Reduced for faster responses
    temperature: 0.6,
  });

  const result = await executeWithRetry(apiCall, `${assetType} Analysis`);
  
  if (!result?.success) {
    // Smart fallback analysis based on market data
    const priceDirection = (marketData?.change24h || 0) > 0 ? 'positive' : 'negative';
    const volatility = Math.abs(marketData?.change24h || 0) > 5 ? 'high' : 'moderate';
    
    const analysisTemplates = {
      crypto: `${symbol} Analysis: Current price movement shows ${priceDirection} momentum with ${volatility} volatility. The 24h change of ${(marketData?.change24h || 0)?.toFixed(2)}% indicates ${Math.abs(marketData?.change24h || 0) > 3 ? 'significant' : 'moderate'} market activity. Trading volume suggests ${(marketData?.volume24h || 0) > 1000000 ? 'strong' : 'moderate'} interest. Consider market conditions and risk tolerance when making trading decisions.`,
      stocks: `${symbol} Analysis: Stock shows ${priceDirection} performance with ${volatility} volatility patterns. The current price movement of ${(marketData?.change24h || 0)?.toFixed(2)}% aligns with ${Math.abs(marketData?.change24h || 0) > 2 ? 'above-average' : 'normal'} market dynamics. Volume indicators suggest ${(marketData?.volume24h || 0) > 500000 ? 'institutional' : 'retail'} interest levels. Fundamental analysis and market conditions should guide investment decisions.`
    };
    
    const errorMessage = result?.userMessage || 'AI analysis service temporarily unavailable';
    const reason = result?.type === 'AUTHENTICATION_ERROR' ? 'API authentication failed' :
                   result?.type === 'RATE_LIMIT_EXCEEDED' ? 'API quota exceeded' :
                   result?.type === 'CONFIGURATION_ERROR'? 'API key not configured' : 'Service temporarily unavailable';
    
    return {
      symbol,
      assetType,
      analysis: `Fast Technical Analysis: ${analysisTemplates?.[assetType]} \n\nNote: Full AI analysis available with OpenAI API configuration.`,
      timestamp: new Date()?.toISOString(),
      fallback: true,
      error: result,
      errorMessage,
      reason,
      quickNote: 'Using technical analysis patterns - enhanced AI insights available with API key'
    };
  }

  return {
    symbol,
    assetType,
    analysis: result?.data?.choices?.[0]?.message?.content,
    timestamp: new Date()?.toISOString(),
    fallback: false
  };
}

/**
 * Streams a chat completion response chunk by chunk.
 * @param {string} userMessage - The user's input message.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {Function} onError - Callback to handle errors.
 */
export async function getStreamingChatCompletion(userMessage, onChunk, onError) {
  const apiCall = async () => {
    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: 'You are a helpful financial AI assistant.' },
        { role: 'user', content: userMessage },
      ],
      stream: true,
      max_tokens: 1000,
    });

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        onChunk(content);
      }
    }
  };

  const result = await executeWithRetry(apiCall, 'Streaming Chat');
  
  if (!result?.success && onError) {
    onError(result);
    // Send fallback message in chunks
    const fallbackMessage = `${result?.userMessage || 'AI service temporarily unavailable'}\n\nFallback: I'm currently experiencing technical difficulties but I'm here to help with your trading and investment questions to the best of my ability.`;
    const words = fallbackMessage?.split(' ');
    
    for (const word of words) {
      await delay(50); // Simulate streaming
      onChunk(word + ' ');
    }
  }
}

/**
 * Generates trading signals based on market data.
 * @param {Object} marketData - Current market data.
 * @returns {Promise<Object>} AI-generated trading signals.
 */
export async function generateTradingSignals(marketData) {
  const prompt = `Analyze the following market data and generate trading signals:
    ${JSON.stringify(marketData, null, 2)}
    
    Provide specific buy/sell/hold recommendations with reasoning.`;

  const model = getConfiguredModel(); // Use configured model
  
  const apiCall = () => openai?.chat?.completions?.create({
    model,
    messages: [
      { 
        role: 'system', 
        content: 'You are an expert trading signal generator. Analyze market data and provide actionable trading recommendations with clear entry/exit points and risk management.' 
      },
      { role: 'user', content: prompt },
    ],
    response_format: { type: 'json_object' },
  });

  const result = await executeWithRetry(apiCall, 'Trading Signals');
  
  if (!result?.success) {
    const errorMessage = result?.userMessage || 'AI trading signals service temporarily unavailable';
    const reason = result?.type === 'AUTHENTICATION_ERROR' ? 'API authentication failed' :
                   result?.type === 'RATE_LIMIT_EXCEEDED' ? 'API quota exceeded' :
                   result?.type === 'CONFIGURATION_ERROR'? 'API key not configured' : 'Service temporarily unavailable';

    // Return fallback trading signals
    return {
      signals: [
        {
          symbol: 'BTC',
          action: 'HOLD',
          confidence: 60,
          reasoning: `${errorMessage} - Using conservative fallback signal based on general market conditions.`,
          timeframe: '1 day'
        }
      ],
      marketSentiment: 'Neutral - AI analysis temporarily unavailable',
      riskAssessment: 'Medium to High - Exercise caution and use proper risk management',
      fallback: true,
      error: result,
      errorMessage,
      reason
    };
  }

  try {
    const parsedResult = JSON.parse(result?.data?.choices?.[0]?.message?.content);
    return { ...parsedResult, fallback: false };
  } catch (parseError) {
    console.error('Error parsing trading signals response:', parseError);
    return {
      signals: [
        {
          symbol: 'BTC',
          action: 'HOLD',
          confidence: 50,
          reasoning: 'Response parsing failed. Using conservative fallback signal.',
          timeframe: '1 day'
        }
      ],
      marketSentiment: 'Unknown - Parsing error occurred',
      riskAssessment: 'High - Unable to analyze current conditions',
      fallback: true,
      error: { type: 'PARSE_ERROR', message: parseError?.message },
      errorMessage: 'Response parsing failed',
      reason: 'JSON parsing error'
    };
  }
}

/**
 * Analyzes an image using GPT-4 Vision capabilities.
 * @param {string} imageUrl - URL of the image to analyze.
 * @param {string} analysisType - Type of analysis to perform.
 * @returns {Promise<Object>} Detailed image analysis.
 */
export async function analyzeImage(imageUrl, analysisType = 'general') {
  const systemPrompts = {
    general: 'Provide a comprehensive analysis of this image, describing what you see and any relevant insights.',
    technical: 'Analyze this image from a technical perspective, focusing on charts, graphs, or technical indicators if present.',
    market: 'If this image contains market data, charts, or trading information, provide detailed analysis and insights.',
  };

  const apiCall = () => openai?.chat?.completions?.create({
    model: 'gpt-4o-mini',
    messages: [
      { 
        role: 'system', 
        content: systemPrompts?.[analysisType] || systemPrompts?.general
      },
      {
        role: 'user',
        content: [
          { type: 'text', text: `Please analyze this image with focus on: ${analysisType}` },
          { type: 'image_url', image_url: { url: imageUrl } },
        ],
      },
    ],
    max_tokens: 1000,
  });

  const result = await executeWithRetry(apiCall, 'Image Analysis');
  
  if (!result?.success) {
    const errorMessage = result?.userMessage || 'AI image analysis service temporarily unavailable';
    const reason = result?.type === 'AUTHENTICATION_ERROR' ? 'API authentication failed' :
                   result?.type === 'RATE_LIMIT_EXCEEDED' ? 'API quota exceeded' :
                   result?.type === 'CONFIGURATION_ERROR'? 'API key not configured' : 'Service temporarily unavailable';

    return {
      analysis: `${errorMessage}\n\nImage analysis is currently unavailable. Please try uploading the image again or contact support if the issue persists.`,
      analysisType,
      imageUrl,
      timestamp: new Date()?.toISOString(),
      fallback: true,
      error: result,
      errorMessage,
      reason
    };
  }

  return {
    analysis: result?.data?.choices?.[0]?.message?.content,
    analysisType,
    imageUrl,
    timestamp: new Date()?.toISOString(),
    fallback: false
  };
}

/**
 * Checks if the input text violates content policies.
 * @param {string} text - The text to moderate.
 * @returns {Promise<object>} Moderation results.
 */
export async function moderateText(text) {
  const apiCall = () => openai?.moderations?.create({
    model: 'text-moderation-latest',
    input: text,
  });

  const result = await executeWithRetry(apiCall, 'Text Moderation');
  
  if (!result?.success) {
    // Return safe fallback for moderation
    return {
      flagged: false,
      categories: {},
      category_scores: {},
      error: result?.userMessage || 'Moderation service temporarily unavailable',
      fallback: true
    };
  }

  return { ...result?.data?.results?.[0], fallback: false };
}

/**
 * Checks the current API quota and usage status
 * @returns {Promise<Object>} Quota information and recommendations
 */
export async function checkAPIQuota() {
  // This would typically require a separate API call to OpenAI's usage endpoint // For now, we'll return general guidance
  return {
    status: 'unknown',
    message: 'Unable to check current quota. If you encounter rate limiting, consider upgrading your OpenAI plan.',
    recommendations: [
      'Monitor your API usage in the OpenAI dashboard',
      'Implement request batching for bulk operations',
      'Consider caching responses for frequently requested data',
      'Use appropriate rate limiting in your application'
    ]
  };
}

/**
 * Enhanced API testing function for comprehensive diagnostics
 * @returns {Promise<Object>} Test results with detailed feedback
 */
export async function runDiagnosticTest() {
  console.log('🔍 Running comprehensive OpenAI API diagnostics...');
  
  try {
    const authTest = await testAuthentication();
    
    const diagnosticResult = {
      success: authTest?.success,
      message: authTest?.success ? 
        'OpenAI API configured and ready!' : (authTest?.message || 'OpenAI API authentication failed - check configuration'),
      details: authTest,
      timestamp: new Date()?.toISOString(),
      fallbacksActive: !authTest?.success
    };

    if (authTest?.success) {
      console.log('✅ OpenAI diagnostics passed:', diagnosticResult);
    } else {
      console.warn('⚠️ OpenAI diagnostics failed:', diagnosticResult);
      console.group('🔧 Troubleshooting Information:');
      if (authTest?.suggestions) {
        console.log('📋 Suggestions:');
        authTest?.suggestions?.forEach((suggestion, index) => {
          console.log(`${index + 1}. ${suggestion}`);
        });
      }
      console.groupEnd();
    }
    
    return diagnosticResult;
  } catch (error) {
    console.error('❌ Diagnostic test encountered an error:', error);
    return {
      success: false,
      message: 'Diagnostics encountered an error - fallback responses active',
      fallbacksActive: true,
      timestamp: new Date()?.toISOString(),
      error: error?.message || 'Unknown diagnostic error'
    };
  }
}

// Export enhanced error handler for external use
export { handleOpenAIError, validateConfigurationBeforeCall };